import { z } from 'zod';
import { normalizeFieldErrorKeys } from '../contract/fieldErrors';
import { createAppError } from '../errors/appError';

const ErrorEnvelopeSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    fieldErrors: z.record(z.string(), z.array(z.string())).nullish(),
  }),
});

export type HttpClient = {
  get: <T>(path: string, schema: z.ZodType<T>) => Promise<T>;
  send: <T>(
    method: 'POST' | 'PUT',
    path: string,
    body: unknown,
    schema: z.ZodType<T>,
  ) => Promise<T>;
  delete: <T>(path: string, schema: z.ZodType<T>) => Promise<T>;
};

type HttpClientOptions = {
  baseUrl: string;
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
  /** リクエストのたびに評価する。null ならトークンなし(ログイン前)。 */
  getToken?: () => string | null;
  /**
   * トークンを付けて送ったのに 401 が返ったとき(セッション切れ)に呼ぶ。
   * 引数は「失敗したリクエストが送ったトークン」。再ログイン後に遅れて届いた古いトークンの 401 を
   * 呼び出し側が見分けられるようにするため。同じトークンで複数のリクエストが 401 になると複数回
   * 呼ばれるので、実装は冪等でなければならない。
   */
  onUnauthorized?: (token: string) => void;
};

const readBody = async (res: Response): Promise<unknown> => {
  const text = await res.text();
  if (text.trim() === '') return undefined;
  try {
    return JSON.parse(text);
  } catch (cause) {
    // エラー応答の本文が JSON でなくても、HTTP ステータスに基づくエラーとして扱う
    if (!res.ok) return undefined;
    throw createAppError({
      code: 'INVALID_RESPONSE',
      message: 'レスポンスがJSONではありません',
      status: res.status,
      cause,
    });
  }
};

const describeIssues = (error: z.ZodError): string =>
  error.issues
    .slice(0, 3)
    .map((issue) => `${issue.path.map(String).join('.') || '(root)'}: ${issue.message}`)
    .join('; ');

export const createHttpClient = ({
  baseUrl,
  // fetch は呼び出し時に解決する(MSW が globalThis.fetch を差し替えるため)
  fetchImpl = (input, init) => globalThis.fetch(input, init),
  timeoutMs = 15_000,
  getToken = () => null,
  onUnauthorized,
}: HttpClientOptions): HttpClient => {
  const request = async <T>(
    method: string,
    path: string,
    body: unknown,
    schema: z.ZodType<T>,
  ): Promise<T> => {
    const token = getToken();
    // AbortSignal.timeout はタイムアウト時に自動で中断する(タイマーの後始末が要らない)
    const signal = AbortSignal.timeout(timeoutMs);

    let res: Response;
    try {
      res = await fetchImpl(`${baseUrl}${path}`, {
        method,
        signal,
        headers: {
          Accept: 'application/json',
          ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
          ...(token === null ? {} : { Authorization: `Bearer ${token}` }),
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    } catch (cause) {
      throw createAppError(
        signal.aborted
          ? { code: 'TIMEOUT', message: 'リクエストがタイムアウトしました', cause }
          : { code: 'NETWORK', message: 'ネットワークエラーが発生しました', cause },
      );
    }

    const payload = await readBody(res);

    if (!res.ok) {
      // トークンを付けて送ったのに 401 = セッション切れ。ログイン失敗(トークンなし)は対象外。
      if (res.status === 401 && token !== null) onUnauthorized?.(token);
      const envelope = ErrorEnvelopeSchema.safeParse(payload);
      throw envelope.success
        ? createAppError({
            code: envelope.data.error.code,
            message: envelope.data.error.message,
            status: res.status,
            fieldErrors: normalizeFieldErrorKeys(envelope.data.error.fieldErrors),
          })
        : createAppError({
            code: res.status === 404 ? 'NOT_FOUND' : 'HTTP_ERROR',
            message: `HTTP ${res.status}`,
            status: res.status,
          });
    }

    const parsed = schema.safeParse(payload);
    if (!parsed.success) {
      throw createAppError({
        code: 'INVALID_RESPONSE',
        message: describeIssues(parsed.error),
        status: res.status,
        cause: parsed.error,
      });
    }
    return parsed.data;
  };

  return {
    get: (path, schema) => request('GET', path, undefined, schema),
    send: (method, path, body, schema) => request(method, path, body, schema),
    delete: (path, schema) => request('DELETE', path, undefined, schema),
  };
};
